import {
    EMAIL_SLIDE,
    NAME_SLIDE,
    PAUSE_SLIDE,
    PLAN_LOADER_SLIDE,
    QUESTION_ANSWER_SLIDE_COMPONENT,
    RESULT_SLIDE,
    START_SLIDE
} from '../quiz-component-types';
import { BLOCKS, CLOSE, FEEL, SMELLS } from '../memory-map-data';
import { AGE_QUESTION_ID, CHAPTER_PICK_ID, CLOSE_QUESTION_IDS, GENDER_QUESTION_ID, NAME_QUESTION_ID, getBlockQuestionId } from '../memory-map-helpers';

const ABOUT_YOU_CATEGORY = 'About you';
const LAST_TWO_CATEGORY = 'Last two';
const CLARITY_HINT = 'Move the slider';
const GRID_HINT = 'Choose one';
const SMELLS_HINT = 'Choose all that apply';
const SOUND_HINT = 'Press play. Headphones help.';
const SKIP_SOUND_LABEL = 'Continue without sound';

const toAnswers = options => options.map(([text], index) => ({ id: index + 1, text }));
const toFeelAnswers = () => FEEL.map(([text], index) => ({ id: index + 1, text }));

const AGE_ANSWERS = ['18–24', '25–34', '35–44', '45–54', '55–64', '65+'].map((text, index) => ({ id: index + 1, text }));
const GENDER_ANSWERS = ['Woman', 'Man', 'Prefer not to say'].map((text, index) => ({ id: index + 1, text }));

const INTRO_ID_BASE = 5981;
const DONE_ID_BASE = 5985;

function getQuestionSlide(block, blockIndex, question, questionIndex) {
    const id = getBlockQuestionId(blockIndex, questionIndex);
    const category = block.short;
    const slide = { id, component: QUESTION_ANSWER_SLIDE_COMPONENT };

    switch (question.t) {
        case 'clarity':
            return {
                ...slide,
                kind: 'clarity',
                continueButton: { isVisible: true },
                props: { category, question: question.q, hint: CLARITY_HINT, imageId: question.img, ends: question.ends }
            };
        case 'feel':
            return {
                ...slide,
                kind: 'text',
                props: { category, question: question.q, hint: question.hint, imageId: question.img, answers: toFeelAnswers() }
            };
        case 'grid':
            return {
                ...slide,
                kind: 'grid',
                props: {
                    category,
                    question: question.q,
                    hint: GRID_HINT,
                    answers: question.o.map(([imageId, text], index) => ({ id: index + 1, text, imageId }))
                }
            };
        case 'single':
            return {
                ...slide,
                kind: 'text',
                props: { category, question: question.q, imageId: question.img, answers: toAnswers(question.o) }
            };
        case 'sound':
            return {
                ...slide,
                kind: 'sound',
                props: {
                    category,
                    question: question.q,
                    hint: SOUND_HINT,
                    soundId: question.snd,
                    skipLabel: SKIP_SOUND_LABEL,
                    answers: toAnswers(question.o)
                }
            };
        case 'choice':
            return {
                ...slide,
                kind: 'text',
                props: { category, question: question.q, answers: toAnswers(question.o) }
            };
        case 'smells':
            return {
                ...slide,
                kind: 'smells',
                continueButton: { isVisible: true },
                props: {
                    category,
                    question: question.q,
                    hint: SMELLS_HINT,
                    groups: [
                        { key: 'good', title: 'Takes me back', icon: 'sun', items: SMELLS.good.map(([icon, text]) => ({ icon, text })) },
                        { key: 'bad', title: 'I still can’t stand it', icon: 'cloud-with-lightning', items: SMELLS.bad.map(([icon, text]) => ({ icon, text })) }
                    ]
                }
            };
        default:
            return {
                ...slide,
                kind: 'sign',
                continueButton: { isVisible: true },
                props: { category, question: question.q, hint: CLARITY_HINT, symbol: question.t, ends: question.ends }
            };
    }
}

function getBlockSlides(block, blockIndex) {
    return [
        {
            id: INTRO_ID_BASE + blockIndex,
            component: PAUSE_SLIDE,
            isNotIndexing: true,
            continueButton: { isVisible: true },
            props: { variant: 'v30', blockIndex }
        },
        ...block.q.map((question, questionIndex) => getQuestionSlide(block, blockIndex, question, questionIndex)),
        {
            id: DONE_ID_BASE + blockIndex,
            component: PAUSE_SLIDE,
            isNotIndexing: true,
            continueButton: { isVisible: true },
            props: { variant: 'v31', blockIndex }
        }
    ];
}

const QUIZ_SLIDES_V42 = [
    {
        id: 5900,
        component: START_SLIDE,
        isNotIndexing: true
    },
    {
        id: AGE_QUESTION_ID,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        kind: 'text',
        props: { category: ABOUT_YOU_CATEGORY, question: 'How old are you?', answers: AGE_ANSWERS }
    },
    {
        id: GENDER_QUESTION_ID,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        kind: 'text',
        props: { category: ABOUT_YOU_CATEGORY, question: 'How do you describe yourself?', answers: GENDER_ANSWERS }
    },
    ...BLOCKS.flatMap(getBlockSlides),
    ...CLOSE.map((question, index) => ({
        id: CLOSE_QUESTION_IDS[index],
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        kind: 'text',
        props: { category: LAST_TWO_CATEGORY, question: question.q, answers: toAnswers(question.o) }
    })),
    {
        id: NAME_QUESTION_ID,
        component: NAME_SLIDE,
        isNotIndexing: true
    },
    {
        id: 5961,
        component: PLAN_LOADER_SLIDE,
        isNotIndexing: true,
        isNotBackButton: true,
        props: { steps: ['Counting what came back…', 'Weighing each chapter…', 'Finding what doesn’t let go…', 'Drawing your map…'] }
    },
    {
        id: 5962,
        component: EMAIL_SLIDE,
        isNotIndexing: true
    },
    {
        id: 5971,
        component: RESULT_SLIDE,
        isNotIndexing: true,
        isNotBackButton: true,
        continueButton: { label: 'Start keeping my memories' },
        visitedEvent: 'PROFILE_PAGE_VISITED',
        props: { variant: 'map' }
    },
    {
        id: CHAPTER_PICK_ID,
        component: RESULT_SLIDE,
        isNotIndexing: true,
        continueButton: { label: 'See my first question' },
        visitedEvent: 'QUIZ_QUESTION_VISITED',
        visitedEventParams: { questionNumber: 27 },
        props: { variant: 'chapter' }
    },
    {
        id: 5972,
        component: RESULT_SLIDE,
        isNotIndexing: true,
        visitedEvent: 'QUIZ_QUESTION_VISITED',
        visitedEventParams: { questionNumber: 28 },
        props: { variant: 'firstQuestion' }
    },
    {
        id: 5973,
        component: RESULT_SLIDE,
        isNotIndexing: true,
        visitedEvent: 'QUIZ_QUESTION_VISITED',
        visitedEventParams: { questionNumber: 29 },
        props: { variant: 'talk' }
    },
    {
        id: 5974,
        component: RESULT_SLIDE,
        isNotIndexing: true,
        continueButton: { label: 'Unlock my memory book' },
        visitedEvent: 'PRESALE_PAGE_VISITED',
        props: { variant: 'book' }
    }
];

export {
    QUIZ_SLIDES_V42
};
